"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import {
  ShopBreadcrumbsProvider,
  useShopBreadcrumbs,
} from "@/integrations/wordpress/WordPressEcommerceShopBreadcrumbs";
import {
  EcommerceTermsProvider,
  useEcommerceTerms,
} from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";
import { Layers, RefreshCw, Wrench } from "lucide-react";

declare const wvcClient: {
  getCurrentWPTermQueryParams: () => Record<string, unknown>;
};

function ShopBreadcrumbsConsumer() {
  const { links, loading, error } = useShopBreadcrumbs();

  if (loading || error || !links || links.length === 0) {
    return (
      <nav aria-label="Breadcrumb fallback" className="mb-4">
        <ol className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground sm:text-sm font-mono uppercase tracking-wider">
          <li>
            <Link to="/" className="hover:text-foreground transition-colors">
              Home
            </Link>
          </li>
          <li aria-hidden="true" className="text-muted-foreground/60">/</li>
          <li>
            <Link to="/ShopPage" className="hover:text-foreground transition-colors">
              Shop
            </Link>
          </li>
          <li aria-hidden="true" className="text-muted-foreground/60">/</li>
          <li className="text-foreground font-semibold">Equipment &amp; Tools</li>
        </ol>
      </nav>
    );
  }

  const lastIndex = links.length - 1;

  return (
    <Breadcrumb className="mb-4">
      <BreadcrumbList className="font-mono text-xs uppercase tracking-wider text-muted-foreground sm:text-sm">
        {links.map((link, index) => (
          <React.Fragment key={`${link.href}-${index}`}>
            <BreadcrumbItem>
              {index === lastIndex ? (
                <BreadcrumbPage className="text-foreground font-semibold">
                  {link.title}
                </BreadcrumbPage>
              ) : (
                <BreadcrumbLink
                  href={link.href}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  {link.title}
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
            {index < lastIndex && <BreadcrumbSeparator />}
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function ArchiveTermsConsumer() {
  const { terms, loading, error, refetch } = useEcommerceTerms();

  if (loading) {
    return (
      <div className="mt-8 flex flex-wrap gap-2 animate-pulse">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            data-index={i}
            className="h-8 w-28 rounded-md bg-muted/40"
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-6 flex items-center gap-3 text-xs text-muted-foreground">
        <span>Unable to retrieve categories.</span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          className="h-7 text-xs"
        >
          <RefreshCw className="mr-1 size-3" />
          Retry
        </Button>
      </div>
    );
  }

  if (!terms || terms.length === 0) {
    return null;
  }

  return (
    <div className="mt-8 border-t border-border/40 pt-6">
      <div className="flex items-center gap-2 mb-3 text-xs uppercase font-mono tracking-widest text-muted-foreground">
        <Layers className="size-3.5 text-primary" />
        <span>Browse Categories</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {terms.map((term, i) => (
          <div key={term.id} data-index={i}>
            {term.link ? (
              <Link
                to={term.link}
                className="inline-flex items-center gap-2 rounded-md border border-border bg-card/60 px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary hover:bg-card"
              >
                <span>{term.name}</span>
                {typeof term.count === "number" && (
                  <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                    {term.count}
                  </span>
                )}
              </Link>
            ) : (
              <span className="inline-flex items-center gap-2 rounded-md border border-border bg-card/60 px-3 py-1.5 text-xs font-medium text-foreground">
                <span>{term.name}</span>
                {typeof term.count === "number" && (
                  <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                    {term.count}
                  </span>
                )}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ArchiveTitle() {
  const current_wp_term_query_params =
    typeof wvcClient !== "undefined" && wvcClient.getCurrentWPTermQueryParams
      ? wvcClient.getCurrentWPTermQueryParams()
      : { taxonomy: "product_cat", per_page: 12 };

  const wp_term_query = new WP_Term_Query(current_wp_term_query_params);

  return (
    <section
      id="archive-title"
      data-nav="dark"
      className="bg-background text-foreground border-b border-border/60 py-10 md:py-14"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col">
          <ShopBreadcrumbsProvider>
            <ShopBreadcrumbsConsumer />
          </ShopBreadcrumbsProvider>

          <div className="mt-2 max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-sm border border-border/80 bg-muted/40 px-2.5 py-1 text-xs font-mono uppercase tracking-wider text-muted-foreground mb-3">
              <Wrench className="size-3 text-primary" />
              <span>Catalog &amp; Equipment</span>
            </div>
            <h1 className="font-serif text-3xl font-bold uppercase tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              Professional Tools &amp; Hardware
            </h1>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground font-sans leading-relaxed">
              Find tested tools, workshop gear, and maintenance equipment built for everyday shop tasks.
            </p>
          </div>

          <EcommerceTermsProvider wp_term_query={wp_term_query}>
            <ArchiveTermsConsumer />
          </EcommerceTermsProvider>
        </div>
      </div>
    </section>
  );
}